import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState
} from "react";

import { keyframes } from "@emotion/react";

import {
  Box,
  CloseButton,
  Dialog,
  Flex,
  Portal,
  VStack
} from "@chakra-ui/react";

import { Button } from "../Button";
import { DotLoader, type DotLoaderPropTypes } from "../DotLoader";
import { Tooltip } from "../Tooltip";

declare global {
  interface Window {
    Intercom?: (command: string, options?: any) => void;
  }
}

interface BaseModalProps {
  title?: string | React.ReactNode;
  primaryBtnTxt?: string | React.ReactNode;
  onPrimaryBtnClick?: () => void;
  isPrimaryBtnLoading?: boolean;
  onSecondaryBtnClick?: () => void;
  resetModalStateOnClose?: () => void;
  infoBanners?: React.ReactNode;
  children: React.ReactNode;
  secondaryBtnTxt?: string | React.ReactNode;
  size?: "xs" | "sm" | "md" | "lg" | "xl" | "2xl" | "3xl" | "full" | "cover";
  shouldHideSecondaryBtn?: boolean;
  shouldHidePrimaryBtn?: boolean;
  isAnimateFromBottom?: boolean;
  isPrimaryBtnDisabled?: boolean;
  isLoading?: boolean;
  errorComponent?: React.ReactNode;
  loaderStyles?: DotLoaderPropTypes;
  primaryBtnTrackingId?: string;
  secondaryBtnTrackingId?: string;
  primaryBtnTooltip?: string;
}

export interface ModalRef {
  show: () => void;
  dismiss: () => void;
  isOpen: boolean;
}

const slideInAnimation = keyframes`
  0% {  transform: translateY(100vh); }
  100% { opacity: 1; transform: translateY(0); }
`;

const slideOutAnimation = keyframes`
  0% { opacity: 1; transform: translateY(0); }
  100% {  transform: translateY(100vh); }
`;

const InternalLoader = (props: DotLoaderPropTypes) => (
  <VStack gap={3} align="center" textAlign="center">
    <Flex gap={6} alignItems="center">
      <DotLoader size="lg" direction="row" {...props} />
    </Flex>
  </VStack>
);

const BaseModal = forwardRef<ModalRef, BaseModalProps>(
  (
    {
      title,
      primaryBtnTxt,
      secondaryBtnTxt = "Cancel",
      size = "lg",
      onPrimaryBtnClick,
      onSecondaryBtnClick,
      infoBanners,
      shouldHideSecondaryBtn = false,
      children,
      isAnimateFromBottom = true,
      isPrimaryBtnLoading = false,
      isPrimaryBtnDisabled = false,
      shouldHidePrimaryBtn = false,
      resetModalStateOnClose,
      isLoading = false,
      errorComponent,
      loaderStyles,
      primaryBtnTrackingId,
      secondaryBtnTrackingId,
      primaryBtnTooltip
    },
    ref
  ) => {
    const [isVisible, setIsVisible] = useState(false);
    const [isClosing, setIsClosing] = useState(false);

    const onOpen = () => {
      setIsVisible(true);
      if (isAnimateFromBottom) setIsClosing(false);
    };
    const onClose = () => {
      setIsVisible(false);
      if (resetModalStateOnClose) resetModalStateOnClose();
      if (isAnimateFromBottom) setIsClosing(true);
    };
    const realHeight = getComputedStyle(document.body)
      .getPropertyValue("--vh")
      .slice(0, -2);
    const modalRef = useMemo<ModalRef>(
      () => ({
        show: onOpen,
        dismiss: onClose,
        isOpen: isVisible
      }),
      [onOpen, onClose, isVisible]
    );

    useImperativeHandle(ref, () => modalRef, [modalRef]);

    useEffect(() => {
      if (window.Intercom) {
        window.Intercom("update", {
          hide_default_launcher: isVisible
        });
      }
    }, [isVisible]);

    return (
      <Dialog.Root
        data-bark-component="BaseModal"
        open={isVisible}
        onOpenChange={onClose}
        size={{ base: "xl", md: size }}
        scrollBehavior="inside"
        onInteractOutside={e => {
          e.preventDefault();
        }}
      >
        <Portal>
          <Dialog.Backdrop height={`${realHeight}px`} zIndex={2147483002} />
          <Dialog.Positioner zIndex={2147483002}>
            <Dialog.Content
              borderRadius={{
                base: "1rem 1rem 0 0",
                md: "1rem"
              }}
              margin={{
                base: "auto 0 0 0",
                md: "auto"
              }}
              mb={{ base: 0, md: "auto" }}
              maxHeight={{ base: "95dvh", md: "86vh" }}
              overflow={{
                base: "hidden",
                lg: isLoading ? "inherit" : "visible"
              }}
              // containerProps={{
              //     zIndex: 2147483002
              // }}
              animation={{
                base: `${!isVisible ? slideOutAnimation : slideInAnimation} 0.3s ${!isVisible ? "ease-in" : "ease-out"}`,
                md: isAnimateFromBottom
                  ? `${isClosing ? slideOutAnimation : slideInAnimation} 0.3s ${isClosing ? "ease-in" : "ease-out"}`
                  : "normal"
              }}
            >
              {(!isLoading || errorComponent) && (
                <Flex
                  flex={1}
                  flexDirection="row"
                  justifyContent="space-between"
                  alignItems="center"
                  background="canvas"
                  borderBottom="1px solid"
                  borderColor="lines"
                  borderRadius="1rem 1rem 0 0"
                >
                  {title && (
                    <Dialog.Header
                      flex={1}
                      fontStyle="heading.small"
                      fontWeight={600}
                      m={0}
                      textAlign="start"
                      padding="20px 24px"
                      color="text"
                    >
                      {title}
                    </Dialog.Header>
                  )}

                  <Dialog.CloseTrigger asChild position="relative" top="0">
                    <CloseButton
                      size="2xl"
                      color="text"
                      ml="auto"
                      _hover={{ backgroundColor: "canvas" }}
                    />
                  </Dialog.CloseTrigger>
                </Flex>
              )}

              {isLoading && !errorComponent && (
                <Flex
                  position="absolute"
                  left={0}
                  right={0}
                  top={0}
                  bottom={0}
                  align="center"
                  justify="center"
                  bg="canvas"
                  zIndex={1}
                  pointerEvents="all"
                  borderRadius={{ base: "1rem 1rem 0 0", md: "1rem" }}
                >
                  <InternalLoader {...loaderStyles} />
                </Flex>
              )}

              {errorComponent && (
                <Flex flex="1" align="center" justify="center" p={6}>
                  {errorComponent}
                </Flex>
              )}
              {!errorComponent && <Dialog.Body p={0}>{children}</Dialog.Body>}
              {!isLoading && !errorComponent && (
                <Dialog.Footer
                  borderTop="1px solid"
                  borderColor="lines"
                  background="canvas"
                  borderRadius={{
                    base: "0",
                    lg: "0 0 1rem 1rem"
                  }}
                >
                  <Flex
                    flex={1}
                    gap={2}
                    flexDirection={{
                      base: "column",
                      lg: "column"
                    }}
                  >
                    {infoBanners}
                    <Flex
                      width="100%"
                      flexDirection="row"
                      justifyContent={
                        shouldHideSecondaryBtn ? "flex-end" : "space-between"
                      }
                      alignItems="center"
                      gap={2}
                      mt={2}
                    >
                      {!shouldHideSecondaryBtn && (
                        <Button
                          w="auto"
                          variant="secondaryHollow"
                          size="sm"
                          py={2}
                          px={4}
                          textAlign="center"
                          minHeight="2.875rem"
                          data-testid="modal-secondary-btn"
                          data-tracking-id={secondaryBtnTrackingId}
                          onClick={onSecondaryBtnClick ?? onClose}
                        >
                          {secondaryBtnTxt}
                        </Button>
                      )}
                      {!shouldHidePrimaryBtn && (
                        <Tooltip
                          content={primaryBtnTooltip ?? ""}
                          disabled={!isPrimaryBtnDisabled || !primaryBtnTooltip}
                          placement="top"
                          contentProps={{ zIndex: "max" }}
                        >
                          <Box
                            as="span"
                            display="inline-block"
                            data-testid="modal-primary-btn-wrapper"
                          >
                            <Button
                              w="auto"
                              variant="secondary"
                              size="sm"
                              py={2}
                              px={4}
                              textAlign="center"
                              minHeight="2.875rem"
                              ml={{ base: 0, lg: 4 }}
                              disabled={isPrimaryBtnDisabled}
                              pointerEvents={
                                isPrimaryBtnDisabled ? "none" : undefined
                              }
                              onClick={onPrimaryBtnClick}
                              loading={isPrimaryBtnLoading}
                              data-testid="modal-primary-btn"
                              data-tracking-id={primaryBtnTrackingId}
                            >
                              {primaryBtnTxt}
                            </Button>
                          </Box>
                        </Tooltip>
                      )}
                    </Flex>
                  </Flex>
                </Dialog.Footer>
              )}
            </Dialog.Content>
          </Dialog.Positioner>
        </Portal>
      </Dialog.Root>
    );
  }
);

export default React.memo(BaseModal);
